import React, { useEffect, useState } from 'react';
import { ExotelConfig, fetchExotelConfig } from '../utils/api';
import './SetupGuide.css';

interface SetupGuideProps {
  userRole: 'super_admin' | 'admin' | 'customer';
}

const SetupGuide: React.FC<SetupGuideProps> = ({ userRole }) => {
  const [config, setConfig] = useState<ExotelConfig | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadConfigData = async () => {
      try {
        const c = await fetchExotelConfig();
        setConfig(c);
      } catch (err) {
        console.error('Failed to load exotel config:', err);
      }
    };
    loadConfigData();
  }, []);

  const handleCopyWebhook = () => {
    if (config?.webhookUrl) {
      navigator.clipboard.writeText(config.webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="setup-guide-view">
      <div className="setup-card-container">
        <div className="setup-card-header">
          <h3>🔌 Connecting Exotel Virtual Numbers</h3>
          <p>Follow these quick configuration steps to get your local server receiving phone calls.</p>
        </div>

        <div className="setup-steps-list">
          {/* Step 1 */}
          <div className="setup-step-item">
            <div className="step-number-circle">1</div>
            <div className="step-body-content">
              <strong>Configure Database Properties</strong>
              <p>Define database connection values inside your server's <code>.env</code> file:</p>
              <pre className="setup-code-block">
{`DB_HOST=localhost
DB_PORT=3306
DB_USER=root
DB_PASSWORD=
DB_NAME=aicall_DB`}
              </pre>
            </div>
          </div>

          {/* Step 2 */}
          <div className="setup-step-item">
            <div className="step-number-circle">2</div>
            <div className="step-body-content">
              <strong>Expose Local Server Port</strong>
              <p>Expose port <code>4005</code> to the internet using ngrok:</p>
              <pre className="setup-code-block">
{`ngrok http 4005`}
              </pre>
              <p className="step-hint-text">
                Then, copy the generated HTTPS forwarding URL and assign it to <code>PUBLIC_BASE_URL</code> in your <code>server/.env</code>.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="setup-step-item">
            <div className="step-number-circle">3</div>
            <div className="step-body-content">
              <strong>Configure Exotel Webhook Applet</strong>
              <p>Create a Passthru Flow applet in your Exotel dashboard and paste this webhook URL:</p>
              {config?.webhookUrl ? (
                <div className="setup-webhook-copy-box">
                  <code>{config.webhookUrl}</code>
                  <button className="copy-webhook-btn" onClick={handleCopyWebhook}>
                    {copied ? '✓ Copied!' : '📋 Copy URL'}
                  </button>
                </div>
              ) : (
                <div className="setup-webhook-copy-box disabled">
                  <code>Set PUBLIC_BASE_URL in your server/.env to generate webhook URL</code>
                </div>
              )}
              <p className="step-hint-text">
                Finally, bind this applet with your virtual ExoPhone number in your Exotel console settings.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="setup-step-item">
            <div className="step-number-circle">4</div>
            <div className="step-body-content">
              <strong>API Settings & Whitelisting</strong>
              <p>
                Ensure your Exotel account SID and credentials are set in the server environment, and your public server IP is whitelisted under Exotel Console API Settings.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SetupGuide;
